import { useState } from 'react';

interface Ensemble {
  id: string;
  name: string;
  description: string;
  members: number;
  image: string;
  details: string;
  videoUrl: string;
}

interface EnsembleCardProps {
  ensemble: Ensemble;
  index: number;
  isVisible: boolean;
}

export default function EnsembleCard({ ensemble, index, isVisible }: EnsembleCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const handleDoubleClick = () => {
    window.open(ensemble.videoUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`relative rounded-lg overflow-hidden h-[500px] transition-all duration-1000 cursor-pointer ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={handleDoubleClick}
      title="ダブルクリックで動画を再生"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={ensemble.image}
          alt={ensemble.name}
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-sumi/95 via-sumi/40 to-transparent"></div>

      {/* Content */}
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
        {/* Members badge */}
        <div className="flex items-center gap-1.5 mb-3">
          <span className="inline-flex items-center gap-1 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-group-line text-xs text-white/80"></i>
            </div>
            <span className="font-sans text-xs text-white/80 tracking-widest">{ensemble.members}名編成</span>
          </span>
        </div>

        <h3 className="font-sans text-2xl md:text-3xl font-bold text-white mb-2 tracking-wide leading-tight">
          {ensemble.name}
        </h3>

        {/* Thin divider */}
        <div className="w-8 h-px bg-white/40 mb-3"></div>

        <p className="font-serif text-sm md:text-base text-white/85 leading-relaxed tracking-wide">
          {ensemble.description}
        </p>
      </div>

      {/* Hover Details Overlay */}
      <div
        className={`absolute inset-0 bg-sumi/95 p-6 md:p-8 flex items-center justify-center transition-all duration-500 ${
          isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="text-center">
          <p className="font-sans text-xs text-white/40 tracking-[0.3em] uppercase mb-3">Ensemble</p>
          <h4 className="font-sans text-xl md:text-2xl font-bold text-white mb-4 leading-snug">
            {ensemble.name}
          </h4>
          <div className="w-8 h-px bg-white/30 mx-auto mb-5"></div>
          <p className="font-serif text-sm md:text-base text-white/85 leading-loose max-w-md mb-8 tracking-wide">
            {ensemble.details}
          </p>
          {/* Double click hint */}
          <div className="flex items-center justify-center gap-2 text-white/40">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-play-circle-line text-base"></i>
            </div>
            <span className="font-sans text-xs tracking-[0.2em] uppercase">Double click to watch</span>
          </div>
        </div>
      </div>
    </div>
  );
}